/**
 * Poseidon dashboard — Strategy and AI research (§3.2 E).
 *
 * Strategy versions, intended regime, entry/exit rules and health; feature
 * freshness and model version; research assessments with source evidence IDs,
 * timestamps, schema-validation result, score, confidence and counter-evidence;
 * prompt/model version and DeepSeek call cost/latency.
 *
 * AI output is labelled "research adjustment" everywhere it appears and is never
 * presented as a price oracle.
 */
import { useState } from 'react';
import type { ResearchAssessmentView, StrategyEligibilityView } from '../api/types';
import { POLL_INTERVALS } from '../config';
import { useApiData } from '../hooks/useApiData';
import { useServices } from '../state/Services';
import { DataTable } from '../components/DataTable';
import type { TableColumn } from '../components/DataTable';
import {
  AiDisclaimer,
  AiResearchBadge,
  Badge,
  Disclosure,
  ErrorState,
  KeyValueList,
  LoadingState,
  Panel,
  Unavailable,
} from '../components/primitives';
import {
  CONVICTION_LABELS,
  COVERAGE_LABELS,
  RECOMMENDATION_LABELS,
  SCHEMA_VALIDATION_LABELS,
  SESSION_BUCKET_LABELS,
  TREND_LABELS,
  VOL_LABELS,
  toneForResearchStatus,
  toneForValidation,
} from '../lib/labels';
import { formatDateTimeWithZone, formatDuration, formatNumber, formatPercent, formatSigned } from '../lib/format';

function StrategyCard({ strategy }: { strategy: StrategyEligibilityView }): JSX.Element {
  return (
    <Panel
      title={strategy.display_name}
      subtitle={`${strategy.version} · ${strategy.enabled ? 'enabled' : 'disabled'}`}
      actions={
        <>
          <Badge tone={strategy.enabled ? 'ok' : 'muted'}>{strategy.enabled ? 'enabled' : 'disabled'}</Badge>
          <Badge tone={strategy.eligible_now ? 'ok' : 'muted'}>{strategy.eligible_now ? 'eligible now' : 'not eligible now'}</Badge>
          <Badge tone="neutral" outline>
            allocation {formatPercent(strategy.risk_allocation.share_of_daily_budget)}
          </Badge>
        </>
      }
      footnote={
        <>
          Allocation policy: <span className="text-mono">{strategy.risk_allocation.policy}</span> — §18.7 keeps this static and
          human-reviewed rather than adaptive. Config version <span className="text-mono">{strategy.config_version}</span>.
        </>
      }
    >
      <div className="grid" style={{ gap: 12 }}>
        <div className="text-muted">{strategy.intended_regime}</div>

        {!strategy.eligible_now && strategy.ineligible_reason !== null && (
          <div className="halt-strip halt-strip--soft" style={{ alignItems: 'flex-start' }}>
            <div>
              <strong>Not eligible to act right now</strong>
              <div style={{ fontSize: 12.5 }}>{strategy.ineligible_reason}</div>
            </div>
          </div>
        )}

        <div className="grid grid--2">
          <div>
            <div className="ribbon__label">Entry rules</div>
            <ul className="consequences" style={{ paddingLeft: 18 }}>
              {strategy.entry_rules.map((rule) => (
                <li key={rule}>{rule}</li>
              ))}
            </ul>
            <div className="ribbon__label" style={{ marginTop: 10 }}>
              Exit rules
            </div>
            <ul className="consequences" style={{ paddingLeft: 18 }}>
              {strategy.exit_rules.map((rule) => (
                <li key={rule}>{rule}</li>
              ))}
            </ul>
          </div>

          <div>
            <div className="ribbon__label">Eligibility (§18.3)</div>
            <KeyValueList
              items={[
                { key: 'Session phase', value: strategy.eligibility.session_phase.map((phase) => SESSION_BUCKET_LABELS[phase]).join(', ') || 'any' },
                { key: 'Regime', value: strategy.eligibility.regime.map((regime) => TREND_LABELS[regime]).join(', ') || 'any' },
                { key: 'Volatility', value: strategy.eligibility.volatility_bucket.map((bucket) => VOL_LABELS[bucket]).join(', ') || 'any' },
                { key: 'Symbols', value: strategy.eligibility.symbols.join(', ') || 'none' },
                {
                  key: 'Post-open warm-up',
                  value:
                    strategy.eligibility.min_minutes_after_open === null
                      ? 'none'
                      : `${strategy.eligibility.min_minutes_after_open} min (gap-risk window, §16.2.1)`,
                },
                { key: 'Max spread', value: strategy.eligibility.max_spread_points === null ? 'inherited' : `${strategy.eligibility.max_spread_points} points` },
                { key: 'Feature version', value: <span className="text-mono">{strategy.model_reference.feature_version}</span> },
                { key: 'Model version', value: <span className="text-mono">{strategy.model_reference.model_version}</span> },
                { key: 'Config hash', value: <span className="text-mono">{strategy.model_reference.config_hash}</span> },
              ]}
            />
          </div>
        </div>

        <div>
          <div className="ribbon__label">Health statistics</div>
          <KeyValueList
            items={[
              { key: 'Trades', value: strategy.health.trades },
              { key: 'Win rate', value: strategy.health.win_rate === null ? <Unavailable /> : formatPercent(strategy.health.win_rate) },
              { key: 'Expectancy', value: strategy.health.expectancy === null ? <Unavailable /> : formatSigned(strategy.health.expectancy) },
              { key: 'Profit factor', value: strategy.health.profit_factor === null ? <Unavailable /> : `${formatNumber(strategy.health.profit_factor, 2)}×` },
              { key: 'Max drawdown', value: strategy.health.max_drawdown === null ? <Unavailable /> : formatNumber(strategy.health.max_drawdown, 2) },
              { key: 'Average R', value: strategy.health.average_r_multiple === null ? <Unavailable /> : `${formatNumber(strategy.health.average_r_multiple, 2)}R` },
              { key: 'Uptime', value: strategy.health.uptime_pct === null ? <Unavailable /> : formatPercent(strategy.health.uptime_pct, 2) },
              { key: 'Last signal', value: formatDateTimeWithZone(strategy.health.last_signal_at) },
              {
                key: 'Missed-trade causes',
                value:
                  strategy.health.missed_trade_causes.length === 0 ? (
                    'none recorded'
                  ) : (
                    <ul style={{ margin: 0, paddingLeft: 16 }}>
                      {strategy.health.missed_trade_causes.map((cause) => (
                        <li key={cause.cause}>
                          {cause.cause}: {cause.count}
                        </li>
                      ))}
                    </ul>
                  ),
              },
            ]}
          />
        </div>
      </div>
    </Panel>
  );
}

function AssessmentDetail({ assessment }: { assessment: ResearchAssessmentView }): JSX.Element {
  return (
    <div className="grid" style={{ gap: 10 }}>
      <div className="data-clock">
        <AiResearchBadge />
        <Badge tone={toneForValidation(assessment.schema_validation.status)}>{SCHEMA_VALIDATION_LABELS[assessment.schema_validation.status]}</Badge>
        <Badge tone={toneForResearchStatus(assessment.expired ? 'stale' : 'valid')}>{assessment.expired ? 'stale' : 'within deadline'}</Badge>
        <Badge tone="info">{assessment.coverage_status}</Badge>
        {assessment.conviction_tier !== null && <Badge tone="neutral">conviction {CONVICTION_LABELS[assessment.conviction_tier]}</Badge>}
      </div>

      <KeyValueList
        items={[
          { key: 'Assessment', value: <span className="text-mono">{assessment.assessment_id}</span> },
          { key: 'Linked decision', value: assessment.decision_id === null ? 'pre-session research, not attached to a signal' : <span className="text-mono">{assessment.decision_id}</span> },
          { key: 'Symbol / as of', value: `${assessment.symbol} · ${formatDateTimeWithZone(assessment.as_of)}` },
          { key: 'Received at', value: formatDateTimeWithZone(assessment.received_at) },
          { key: 'Coverage status', value: COVERAGE_LABELS[assessment.coverage_status] },
          { key: 'Event direction / relevance', value: `${assessment.event_direction} · ${formatNumber(assessment.event_relevance, 3)}` },
          { key: 'Score / confidence', value: `${formatNumber(assessment.research_score, 3)} / ${formatNumber(assessment.confidence, 3)}` },
          { key: 'Recommendation', value: RECOMMENDATION_LABELS[assessment.recommendation] },
          {
            key: 'Bounded adjustment applied',
            value: (
              <span className="text-mono">
                {formatSigned(assessment.applied_adjustment.research_adjustment, 4)} (clamp −{formatNumber(assessment.applied_adjustment.clamp_max, 2)}…+
                {formatNumber(assessment.applied_adjustment.clamp_max, 2)}
                {assessment.applied_adjustment.clamped ? ', clamp engaged' : ''}) · event penalty{' '}
                {formatSigned(assessment.applied_adjustment.event_penalty, 4)}
              </span>
            ),
          },
          {
            key: 'Conviction multiplier',
            value:
              assessment.applied_adjustment.conviction_multiplier === null ? (
                <Unavailable reason="No multiplier applied to this assessment." />
              ) : (
                `${formatNumber(assessment.applied_adjustment.conviction_multiplier, 2)}× — read from the fixed risk table, never set by the model (§19.3)`
              ),
          },
          { key: 'Prompt / model version', value: <span className="text-mono">{assessment.prompt_version} · {assessment.model_version}</span> },
          {
            key: 'Call cost / latency',
            value: `${formatNumber(assessment.call.cost_usd, 5)} USD · ${formatDuration(assessment.call.latency_ms)} · ${assessment.call.prompt_tokens} prompt + ${assessment.call.completion_tokens} completion tokens · status ${assessment.call.status} · attempt ${assessment.call.attempt}`,
          },
          { key: 'Staleness deadline', value: `${formatDateTimeWithZone(assessment.staleness_deadline_at)}${assessment.expired ? ' — passed, treated as neutral' : ''}` },
        ]}
      />

      <div className="grid grid--2">
        <div>
          <div className="ribbon__label">Source evidence</div>
          {assessment.evidence_ids.map((id) => (
            <span className="tag" key={id}>
              {id}
            </span>
          ))}
          {assessment.counter_evidence_ids.length > 0 ? (
            <>
              <div className="ribbon__label" style={{ marginTop: 6 }}>
                Counter-evidence
              </div>
              {assessment.counter_evidence_ids.map((id) => (
                <span className="tag" key={id} style={{ borderColor: 'var(--warn)' }}>
                  {id}
                </span>
              ))}
            </>
          ) : (
            <div className="text-faint" style={{ fontSize: 11.5 }}>
              No counter-evidence supplied.
            </div>
          )}
        </div>
        <div>
          <div className="ribbon__label">Risk flags</div>
          {assessment.risk_flags.length === 0 ? (
            <div className="text-faint" style={{ fontSize: 11.5 }}>
              No risk flags in this assessment.
            </div>
          ) : (
            assessment.risk_flags.map((flag) => (
              <div key={`${flag.type}-${flag.severity}`} style={{ fontSize: 12.5 }}>
                <Badge tone={flag.severity === 'high' ? 'danger' : flag.severity === 'medium' ? 'warn' : 'muted'}>{flag.severity}</Badge>{' '}
                {flag.type.replace(/_/g, ' ')} — [{flag.evidence_ids.join(', ')}]
              </div>
            ))
          )}
          {assessment.proposed_stop_adjustment_pct !== null && (
            <div className="text-faint" style={{ fontSize: 11.5, marginTop: 6 }}>
              Proposed stop/target adjustment: {formatSigned(assessment.proposed_stop_adjustment_pct * 100, 2)}% /{' '}
              {assessment.proposed_target_adjustment_pct === null ? '—' : formatSigned(assessment.proposed_target_adjustment_pct * 100, 2)}% — proposals only;
              the risk engine clamps them to a hard-configured maximum (§19.2).
            </div>
          )}
        </div>
      </div>

      <Disclosure summary={`Bounded reasoning trace (${assessment.reasoning_steps.length} steps)`}>
        <ol style={{ margin: 0, paddingLeft: 18, fontSize: 12.5 }}>
          {assessment.reasoning_steps.map((step) => (
            <li key={step.claim}>
              {step.claim} <span className="text-faint">[{step.evidence_ids.join(', ')}]</span>
            </li>
          ))}
        </ol>
      </Disclosure>

      <div>
        <div className="ribbon__label">Rationale (model text, rendered as untrusted content)</div>
        <blockquote className="disclosure" style={{ margin: 0, fontStyle: 'italic' }}>
          “{assessment.rationale}”
        </blockquote>
      </div>

      {assessment.schema_validation.errors.length > 0 && (
        <div className="dialog__error">
          <strong>Schema validation failed — logged with the raw payload, never silently repaired (§6.4).</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {assessment.schema_validation.errors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function StrategyResearch(): JSX.Element {
  const { api, isMock } = useServices();
  const [selected, setSelected] = useState<string | null>(null);

  const strategies = useApiData('research-strategies', () => api.getStrategies(), { pollMs: POLL_INTERVALS.riskLimits, mode: api.mode });
  const assessments = useApiData('research-assessments', () => api.getResearchAssessments(), { pollMs: POLL_INTERVALS.events, mode: api.mode });
  const freshness = useApiData('research-feature-freshness', () => api.getFeatureFreshness(), { pollMs: POLL_INTERVALS.events, mode: api.mode });
  const costs = useApiData('research-costs', () => api.getResearchCosts(), { pollMs: POLL_INTERVALS.events, mode: api.mode });
  const regimes = useApiData('research-regimes', () => api.getRegimes(), { pollMs: POLL_INTERVALS.events, mode: api.mode });

  const assessmentRows = assessments.data ?? [];
  const selectedAssessment = assessmentRows.find((assessment) => assessment.assessment_id === selected) ?? assessmentRows[0] ?? null;

  const assessmentColumns: readonly TableColumn<ResearchAssessmentView, 'received' | 'symbol' | 'score' | 'confidence' | 'recommendation' | 'validation' | 'latency'>[] = [
    { key: 'received', header: 'Received', sortValue: (row) => row.received_at, render: (row) => <span className="text-mono">{formatDateTimeWithZone(row.received_at)}</span> },
    { key: 'symbol', header: 'Symbol / scope', sortValue: (row) => row.symbol, render: (row) => <>{row.symbol}{row.decision_id === null && <div className="text-faint" style={{ fontSize: 11 }}>pre-session</div>}</> },
    { key: 'score', header: 'Score', align: 'right', sortValue: (row) => row.research_score, render: (row) => <span className="text-mono">{formatNumber(row.research_score, 3)}</span> },
    { key: 'confidence', header: 'Confidence', align: 'right', sortValue: (row) => row.confidence, render: (row) => <span className="text-mono">{formatNumber(row.confidence, 3)}</span> },
    { key: 'recommendation', header: 'Recommendation', sortValue: (row) => row.recommendation, render: (row) => RECOMMENDATION_LABELS[row.recommendation] },
    {
      key: 'validation',
      header: 'Schema / evidence',
      sortValue: (row) => row.schema_validation.status,
      render: (row) => (
        <>
          <Badge tone={toneForValidation(row.schema_validation.status)}>{SCHEMA_VALIDATION_LABELS[row.schema_validation.status]}</Badge>
          <div className="text-faint" style={{ fontSize: 11 }}>
            {row.evidence_ids.length} source · {row.counter_evidence_ids.length} counter
          </div>
        </>
      ),
    },
    {
      key: 'latency',
      header: 'Cost / latency',
      align: 'right',
      sortValue: (row) => row.call.latency_ms,
      render: (row) => (
        <span className="text-mono" style={{ fontSize: 11.5 }}>
          {formatDuration(row.call.latency_ms)}
          <div className="text-faint">{formatNumber(row.call.cost_usd, 5)} USD</div>
        </span>
      ),
    },
  ];

  return (
    <>
      <header className="page-header">
        <div>
          <h1 className="page-header__title">Strategy &amp; AI research</h1>
          <div className="page-header__spec">§3.2 E · strategy versions and eligibility, feature freshness, research assessments with evidence and cost</div>
        </div>
        <div className="data-clock">
          <AiResearchBadge />
          {isMock && <span className="mock-note">mock values</span>}
        </div>
      </header>

      <AiDisclaimer />

      {strategies.error !== null && strategies.data === null ? (
        <ErrorState error={strategies.error} context="Strategy registry could not be loaded." onRetry={strategies.refresh} />
      ) : strategies.data === null ? (
        <LoadingState label="Loading strategies…" />
      ) : (
        strategies.data.map((strategy) => <StrategyCard key={strategy.strategy_id} strategy={strategy} />)
      )}

      <div className="grid grid--2">
        <Panel title="Feature freshness" subtitle="immutable feature snapshots and their cadence" mock={isMock} flush>
          {freshness.data === null ? (
            <LoadingState label="Loading feature freshness…" />
          ) : (
            <DataTable
              rows={freshness.data}
              rowKey={(row) => `${row.symbol}-${row.feature_name}`}
              initialSort={{ key: 'age', direction: 'desc' }}
              columns={[
                { key: 'symbol', header: 'Symbol', sortValue: (row) => row.symbol, render: (row) => row.symbol },
                { key: 'feature', header: 'Feature', sortValue: (row) => row.feature_name, render: (row) => <span className="text-mono">{row.feature_name}</span> },
                { key: 'version', header: 'Version', render: (row) => <span className="text-mono">{row.feature_version}</span> },
                { key: 'age', header: 'Age / cadence', align: 'right', sortValue: (row) => row.age_ms, render: (row) => `${formatDuration(row.age_ms)} / ${formatDuration(row.expected_cadence_ms)}` },
                {
                  key: 'status',
                  header: 'Status',
                  sortValue: (row) => row.status,
                  render: (row) => <Badge tone={row.status === 'fresh' ? 'ok' : row.status === 'aging' ? 'warn' : 'danger'}>{row.status}</Badge>,
                },
              ]}
              emptyTitle="No feature freshness reported"
            />
          )}
        </Panel>

        <Panel
          title="DeepSeek call cost and latency"
          subtitle="§3.2 E · cost/latency per research call, aggregated"
          mock={isMock}
          footnote="The research layer is off the critical execution path: an unavailable, stale or schema-invalid response becomes a neutral adjustment rather than blocking an order (§1.4, §6.1)."
        >
          {costs.data === null ? (
            <LoadingState label="Loading cost summary…" />
          ) : (
            <KeyValueList
              items={[
                { key: 'Window', value: `${formatDateTimeWithZone(costs.data.window_start)} → ${formatDateTimeWithZone(costs.data.window_end)}` },
                { key: 'Calls', value: `${costs.data.calls} (${costs.data.failures} failed)` },
                { key: 'Tokens', value: `${costs.data.total_prompt_tokens} prompt + ${costs.data.total_completion_tokens} completion` },
                { key: 'Total cost', value: `${formatNumber(costs.data.total_cost_usd, 4)} USD` },
                { key: 'Latency', value: `avg ${formatDuration(costs.data.average_latency_ms)} · p95 ${formatDuration(costs.data.p95_latency_ms)}` },
                { key: 'Schema failure rate', value: costs.data.schema_failure_rate === null ? <Unavailable /> : formatPercent(costs.data.schema_failure_rate) },
              ]}
            />
          )}
        </Panel>
      </div>

      <Panel title="Research assessments" subtitle="click a row to inspect evidence, validation and reasoning" mock={isMock} flush>
        {assessments.error !== null && assessments.data === null ? (
          <ErrorState error={assessments.error} context="Research assessments could not be loaded." onRetry={assessments.refresh} />
        ) : assessments.data === null ? (
          <LoadingState label="Loading assessments…" />
        ) : (
          <DataTable
            rows={assessmentRows}
            columns={assessmentColumns}
            rowKey={(row) => row.assessment_id}
            initialSort={{ key: 'received', direction: 'desc' }}
            onRowClick={(row) => setSelected(row.assessment_id)}
            emptyTitle="No research assessments"
            emptyMessage="No assessment means a neutral adjustment, not a blocked order path."
          />
        )}
      </Panel>

      {selectedAssessment !== null && (
        <Panel title="Assessment detail" subtitle={`assessment ${selectedAssessment.assessment_id}`} mock={isMock}>
          <AssessmentDetail assessment={selectedAssessment} />
        </Panel>
      )}

      <Panel title="Live regime snapshots" subtitle="§18.2 deterministic classifier output per instrument" mock={isMock} flush>
        {regimes.data === null ? (
          <LoadingState label="Loading regimes…" />
        ) : (
          <DataTable
            rows={regimes.data}
            rowKey={(row) => `${row.symbol}-${row.bar_time}`}
            columns={[
              { key: 'symbol', header: 'Symbol', sortValue: (row) => row.symbol, render: (row) => <strong>{row.symbol}</strong> },
              { key: 'bar', header: 'Bar time', sortValue: (row) => row.bar_time, render: (row) => <span className="text-mono">{formatDateTimeWithZone(row.bar_time)}</span> },
              { key: 'trend', header: 'Trend', sortValue: (row) => row.trend_strength, render: (row) => TREND_LABELS[row.trend_strength] },
              { key: 'vol', header: 'Volatility', sortValue: (row) => row.volatility_bucket, render: (row) => VOL_LABELS[row.volatility_bucket] },
              { key: 'session', header: 'Session phase', sortValue: (row) => row.session_phase, render: (row) => SESSION_BUCKET_LABELS[row.session_phase] },
              { key: 'inputs', header: 'ADX / vol percentile', align: 'right', render: (row) => <span className="text-mono">{row.inputs.adx === null ? '—' : formatNumber(row.inputs.adx, 2)} / {row.inputs.realized_vol_percentile === null ? '—' : formatNumber(row.inputs.realized_vol_percentile, 3)}</span> },
              { key: 'version', header: 'Version / hash', render: (row) => <span className="text-mono" style={{ fontSize: 11 }}>{row.regime_version}<div className="text-faint">{row.features_hash}</div></span> },
            ]}
            emptyTitle="No regime snapshots"
          />
        )}
      </Panel>

      <Panel title="What the research layer may and may not do" subtitle="§1.4, §6.3, §6.5">
        <div className="grid grid--2">
          <div>
            <div className="ribbon__label">May</div>
            <ul className="consequences" style={{ paddingLeft: 18 }}>
              <li>Extract structured event information from the supplied evidence package.</li>
              <li>Summarise catalysts and counter-evidence, citing evidence IDs.</li>
              <li>Classify sentiment and relevance, with a bounded score and confidence.</li>
              <li>Explain a precomputed signal in this dashboard, labelled as a {`"research adjustment"`}.</li>
              <li>Propose bounded stop/target adjustments, which the risk engine clamps.</li>
            </ul>
          </div>
          <div>
            <div className="ribbon__label">May not</div>
            <ul className="consequences" style={{ paddingLeft: 18 }}>
              <li>Read secrets, keys, local files or the unrestricted database.</li>
              <li>Treat its own knowledge as a price or market-data feed.</li>
              <li>Change risk settings, strategy code or the instrument universe.</li>
              <li>Call <span className="text-mono">order_send</span>, cancel orders or close positions.</li>
              <li>Override a risk rejection or turn a rejected signal into a trade.</li>
            </ul>
          </div>
        </div>
      </Panel>
    </>
  );
}
